'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Delete from '@icons/Delete.svg';
import Email from '@icons/Email.svg';
import Lock from '@icons/Lock.svg';
import { UserStatus } from 'types/collections';
import { RoleType } from 'types/roles';

import { TextStyledLink } from '@components/Common';
import { InformationField } from '@components/InformationField';
import { Input } from '@components/Input';
import { Modal } from '@components/Modal';
import { ActionContainer, ProfileInfoTitle, TitleInfoContainer } from '@components/Profile/styles';
import { Text } from '@components/TextGroups';

import { forgotPassword, signOut } from '@utils/supabase/auth-actions';

interface ProfileActionsProps<UpdateType> {
  role: RoleType;
  deleteRequest: () => Promise<void>;
  updateProfile: (profile: Partial<UpdateType>) => Promise<UpdateType | void>;
}

/**
 * Profile Actions component displays the actions for the profile, such as reset password and delete account.
 */
export function ProfileActions<UpdateType>({
  role,
  deleteRequest,
  updateProfile,
}: ProfileActionsProps<UpdateType>) {
  const { t } = useTranslation('common');

  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [deleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailForReset, setEmailForReset] = useState('');

  const handlePasswordResetRequest = async () => {
    setError(null);
    try {
      await forgotPassword(emailForReset, role);
      setResetPasswordModalOpen(false);
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError(String(error));
      }
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await deleteRequest();
      await updateProfile({ status: UserStatus.DISABLED } as UpdateType);
      setDeleteAccountModalOpen(false);
      await signOut(role);
    } catch (error) {
      console.error('Error requesting account deletion:', error);
    }
  };

  const handleContactJust1Day = () => {
    const email = process.env.NEXT_PUBLIC_SUPPORT_EMAIL || 'info@just1day.nl';
    const subject = encodeURIComponent(t('resetPassword.updateProfileEmailSubject'));
    const mailtoLink = `mailto:${email}?subject=${subject}`;

    // Create a temporary anchor element and trigger click for better security and UX
    const anchor = document.createElement('a');
    anchor.href = mailtoLink;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);

    try {
      anchor.click();
    } finally {
      // Ensure cleanup always happens, even if click() throws an exception
      document.body.removeChild(anchor);
    }
  };

  const resetPasswordModalButtons = [
    {
      label: t('resetPassword.request'),
      onClick: handlePasswordResetRequest,
      variant: 'primary' as const,
    },
  ];

  const deleteAccountModalButtons = [
    {
      label: t('modals.cancel'),
      onClick: () => setDeleteAccountModalOpen(false),
      variant: 'secondary' as const,
    },
    { label: t('modals.deleteAccount'), onClick: handleDeleteAccount, variant: 'primary' as const },
  ];

  return (
    <>
      <TitleInfoContainer>
        <ProfileInfoTitle>{t('account')}</ProfileInfoTitle>
        <ActionContainer>
          <Lock />
          <TextStyledLink onClick={() => setResetPasswordModalOpen(true)}>
            {t('resetPassword.change')}
          </TextStyledLink>
        </ActionContainer>
        <ActionContainer>
          <Email />
          <TextStyledLink onClick={handleContactJust1Day}>
            {t('resetPassword.updateProfile')}
          </TextStyledLink>
        </ActionContainer>
      </TitleInfoContainer>
      <TitleInfoContainer>
        <ProfileInfoTitle>{t('other')}</ProfileInfoTitle>
        <ActionContainer>
          <Delete />
          <TextStyledLink onClick={() => setDeleteAccountModalOpen(true)}>
            {t('deleteAccount')}
          </TextStyledLink>
        </ActionContainer>
      </TitleInfoContainer>

      <Modal
        title={t('resetPassword.changePassword')}
        buttons={resetPasswordModalButtons}
        isOpen={resetPasswordModalOpen}
        onClose={() => setResetPasswordModalOpen(false)}
        fullScreen>
        <Text>{t('resetPassword.description')}</Text>
        <Input
          type="email"
          id="email"
          placeholder={t('enterEmail')}
          name="email"
          label={t('email')}
          hasError={!!error}
          required
          value={emailForReset}
          onChange={(e) => setEmailForReset(e.target.value)}
        />
      </Modal>
      <Modal
        title={t('modals.deleteAccountTitle')}
        buttons={deleteAccountModalButtons}
        isOpen={deleteAccountModalOpen}
        onClose={() => setDeleteAccountModalOpen(false)}>
        <InformationField text={t('modals.deleteAccountDescription')} />
      </Modal>
    </>
  );
}
